import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { GameInfo } from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { updateRobotsTag } from '../../_ui-core_/base/package/util/html-helper';
import { formatNumber } from '../../_ui-core_/base/package/util/number';
import { tick } from '../../_ui-core_/base/package/util/render';
import { RichTextStyleType } from '../../_ui-core_/components/ui-rich-text/ui-rich-text';
import App from '../../app';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { TrackingEventSource } from '../../app/package/base/service/tracking/tracking-service';
import type { Layout } from '../../app/package/base/types';
import style from './styles.css?inline';
import '../../_ui-core_/components/ui-table/ui-table';

const CName = 'rtp-page';

@customElement(CName)
export class RtpPage extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property() headerTitle: string;

  @state() private _layout: Layout;
  @state() private _loadFailed = false;
  @state() private _rtpData: [string, string, string][];

  private readonly _subHelper = new SubHelper();
  private _renderServiceId: number | undefined;

  connectedCallback() {
    super.connectedCallback();
    this._renderServiceId = window.$app.renderService.addTask('rtp-page');
    updateRobotsTag('none');
    App.instance.content
      .gamesInfo([])
      .then((data: GameInfo[]) => {
        if (!this.isConnected) return;
        this._rtpData = data.map((item) => {
          const rtp = item.rtp !== undefined ? formatNumber(item.rtp) : '-';
          return [item.title ?? '-', item.studio ?? '-', `${rtp}%`];
        });

        this._rtpData.sort((a, b) => a[0].localeCompare(b[0]));
        tick().then(() => this._removeTask());

        requestAnimationFrame(() => {
          App.instance.router.scrollToTop();
        });
      })
      .catch((err) => {
        if (!this.isConnected) return;
        window.$app.logger.log('loading games info failed:', err);
        this._loadFailed = true;
      });
    this._subHelper.addSub(
      App.instance.layoutStore,
      (_) => {
        this._layout = _;
      },
      true,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
  }

  protected firstUpdated() {
    this._updateHeader();
  }

  render() {
    const headers = [
      window.$app.$t.get(I18n.gameInfo.gameName),
      window.$app.$t.get(I18n.gameInfo.studio),
      window.$app.$t.get(I18n.gameInfo.rtp),
    ];

    return this._loadFailed ? this._renderFailed() : this._renderMain(headers);
  }

  private _renderFailed() {
    this._removeTask();
    return html`<main><p class="failed">Failed to load</p></main>`;
  }

  private _renderMain(headers: string[]) {
    // we are using rich-text to keep consistent styling on such pages
    const title = `<h1>${window.$app.$t.get(I18n.footer.links.payoutPercentages)}</h1>`;
    return html`
      <main class=${this._layout}>
        <ui-rich-text .content=${title} .styleType=${RichTextStyleType.PAGE}></ui-rich-text>
        <ui-table class="table" .headers=${headers} .rows=${this._rtpData}></ui-table>
      </main>
    `;
  }

  private _removeTask() {
    if (this._renderServiceId === undefined) {
      return;
    }
    window.$app.renderService.removeTask(this._renderServiceId);
    this._renderServiceId = undefined;
  }

  private _updateHeader() {
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: this.headerTitle,
      trackingSource: TrackingEventSource.RTP,
      isGoBackShown: true,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: RtpPage;
  }
}
